import React, { useMemo } from "react";
import { Polyline } from "@react-google-maps/api";

export type RouteStatus = "pending" | "active" | "completed" | "preview";
export type RouteSegmentType = "outbound" | "return";

interface RoutePolylineProps {
  path?: google.maps.LatLngLiteral[];
  encodedPath?: string | null;
  status?: RouteStatus;
  segmentType?: RouteSegmentType;
  showArrows?: boolean;
  strokeWeight?: number;
  strokeOpacity?: number;
  zIndex?: number;
  onClick?: () => void;
}

/**
 * Route style configurations
 * Return routes use dashed pattern with distinct colors for clear visual differentiation
 */
const routeStyles: Record<
  RouteStatus,
  {
    strokeColor: string;
    returnStrokeColor: string;
    strokeWeight: number;
    strokeOpacity: number;
    returnStrokeOpacity: number;
  }
> = {
  pending: {
    strokeColor: "#3458a4",       // Dark blue for outbound
    returnStrokeColor: "#7c9fd4", // Medium blue for return (dashed)
    strokeWeight: 4,
    strokeOpacity: 0.9,
    returnStrokeOpacity: 0.85,
  },
  active: {
    strokeColor: "#52c41a",       // Green for outbound
    returnStrokeColor: "#13a8a8", // Teal/cyan for return (distinct from green, dashed)
    strokeWeight: 5,
    strokeOpacity: 1,
    returnStrokeOpacity: 0.9,
  },
  completed: {
    strokeColor: "#8c8c8c",       // Gray for outbound
    returnStrokeColor: "#a0a0a0", // Slightly different gray for return (dashed)
    strokeWeight: 3,
    strokeOpacity: 0.8,
    returnStrokeOpacity: 0.7,
  },
  preview: {
    strokeColor: "#fa8c16",       // Orange for outbound
    returnStrokeColor: "#d48806", // Darker gold for return (distinct from orange, dashed)
    strokeWeight: 4,
    strokeOpacity: 0.8,
    returnStrokeOpacity: 0.85,
  },
};

/**
 * Get arrow icon configuration for direction indication
 * Created as a function to ensure google maps is loaded
 */
const getArrowIcon = (fillColor: string): google.maps.Symbol => ({
  path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW ?? 0,
  scale: 3,
  strokeColor: "#fff",
  strokeWeight: 1,
  fillColor,
  fillOpacity: 1,
});

/**
 * RoutePolyline Component
 * Draws a route line on the map with directional arrows
 */
const RoutePolyline: React.FC<RoutePolylineProps> = ({
  path: pathProp = [],
  encodedPath,
  status = "pending",
  segmentType = "outbound",
  showArrows = true,
  strokeWeight,
  strokeOpacity,
  zIndex = 1,
  onClick,
}) => {
  const style = routeStyles[status];
  const isReturn = segmentType === "return";

  // Decode encoded polyline if provided, otherwise use path prop
  const path = useMemo<google.maps.LatLngLiteral[]>(() => {
    if (encodedPath && window.google?.maps?.geometry?.encoding) {
      try {
        const decodedPath = window.google.maps.geometry.encoding.decodePath(encodedPath);
        return decodedPath.map((latLng) => ({
          lat: latLng.lat(),
          lng: latLng.lng(),
        }));
      } catch (error) {
        console.error("Error decoding polyline:", error);
        return pathProp;
      }
    }
    return pathProp;
  }, [encodedPath, pathProp]);

  const polylineOptions = useMemo(() => {
    const effectiveColor = isReturn ? style.returnStrokeColor : style.strokeColor;
    const effectiveOpacity = isReturn ? style.returnStrokeOpacity : style.strokeOpacity;
    const effectiveWeight = strokeWeight ?? (isReturn ? style.strokeWeight - 1 : style.strokeWeight);

    const options: google.maps.PolylineOptions = {
      strokeColor: effectiveColor,
      strokeWeight: effectiveWeight,
      strokeOpacity: isReturn ? 0 : (strokeOpacity ?? effectiveOpacity), // Dashed lines use 0 opacity
      zIndex: isReturn ? zIndex - 1 : zIndex,
      clickable: !!onClick,
    };

    // Return routes use dashed pattern for clear visual distinction
    if (isReturn) {
      options.icons = [
        // Dashed line pattern
        {
          icon: {
            path: "M 0,-1 0,1",
            strokeOpacity: effectiveOpacity,
            strokeColor: effectiveColor,
            scale: effectiveWeight,
          },
          offset: "0",
          repeat: "16px",
        },
      ];

      // Add arrow icons for direction
      if (showArrows && path.length > 1) {
        options.icons.push({
          icon: getArrowIcon(effectiveColor),
          offset: "100%",
          repeat: "80px",
        });
      }
    } else if (showArrows && path.length > 1) {
      // Outbound route: solid line with arrows
      options.icons = [
        {
          icon: getArrowIcon(effectiveColor),
          offset: "100%",
          repeat: "80px",
        },
      ];
    }

    return options;
  }, [style, strokeWeight, strokeOpacity, zIndex, showArrows, path.length, onClick, isReturn]);

  if (path.length < 2) {
    return null;
  }

  return <Polyline path={path} options={polylineOptions} onClick={onClick} />;
};

/**
 * DashedPolyline Component
 * For showing suggested routes or connections
 */
export const DashedPolyline: React.FC<{
  path: google.maps.LatLngLiteral[];
  strokeColor?: string;
  strokeWeight?: number;
  zIndex?: number;
}> = ({
  path,
  strokeColor = "#3458a4",
  strokeWeight = 2,
  zIndex = 5,
}) => {
  const options = useMemo(
    () => ({
      strokeColor,
      strokeWeight,
      strokeOpacity: 0,
      zIndex,
      icons: [
        {
          icon: {
            path: "M 0,-1 0,1",
            strokeOpacity: 0.8,
            strokeColor,
            scale: strokeWeight,
          },
          offset: "0",
          repeat: "12px",
        },
      ],
    }),
    [strokeColor, strokeWeight, zIndex]
  );

  if (path.length < 2) {
    return null;
  }

  return <Polyline path={path} options={options} />;
};

/**
 * AnimatedPolyline Component
 * For highlighting routes with animation
 */
export const AnimatedPolyline: React.FC<{
  path: google.maps.LatLngLiteral[];
  strokeColor?: string;
}> = ({ path, strokeColor = "#52c41a" }) => {
  const options = useMemo(
    () => ({
      strokeColor,
      strokeWeight: 4,
      strokeOpacity: 0.8,
      zIndex: 10,
      icons: [
        {
          icon: {
            path: window.google?.maps?.SymbolPath?.CIRCLE ?? 0,
            scale: 4,
            fillColor: "#fff",
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor,
          },
          offset: "0%",
        },
      ],
    }),
    [strokeColor]
  );

  if (path.length < 2) {
    return null;
  }

  return <Polyline path={path} options={options} />;
};

export default React.memo(RoutePolyline);
